import { get_user_details } from "@/router";
import HomeNavBar from "@/components/HomeNavBar/HomeNavBar.vue";
import {
  is_organization_admin_role,
  normalize_organization_profile,
} from "@/utils/organization_profile";
import organization_members_access_methods from "./organization_members_access_methods";
import organization_members_context_methods from "./organization_members_context_methods";
import organization_members_form_methods from "./organization_members_form_methods";
import organization_members_company_methods from "./organization_members_company_methods";
import {
  member_is_active,
  member_is_accepted,
  member_is_pending,
  member_is_rejected,
  format_invitation_date,
  get_organization_member_by_user_details,
  member_has_feature_permission,
} from "./organization_members_helpers";

function get_company_stats(companies) {
  const active = companies.filter((company) => company.is_active !== false).length;
  return {
    total: companies.length,
    active,
    pending: 0,
    inactive: companies.length - active,
  };
}

export default {
  components: {
    HomeNavBar,
  },
  data: () => ({
    user_details: {},
    active_profile: null,
    organization_members: [],
    organization_companies: [],
    is_loading_companies: false,
    is_creating_company: false,
    is_updating_company: false,
    editing_company_id: null,
    selected_company_id: null,
    active_management_tab: "members",
    new_company: {
      company_name: "",
      description: "",
      email: "",
      tell_number: "",
      location: "",
    },
    company_errors: {},
    company_location_autocomplete: null,
    company_location_autocomplete_input: null,
    organization_features: [],
    organization_permissions: [],
    search_query: "",
    members_table_instance: null,
    companies_table_instance: null,
    invitations_table_instance: null,
    is_loading_members: false,
    is_loading_access_options: false,
    is_creating_user: false,
    is_saving_access: false,
    is_inviting_member: false,
    is_bulk_adding_members: false,
    is_assigning_existing_member: false,
    selected_member: null,
    saved_branding: null,
    member_access: {
      features: [],
      permission_ids: [],
      feature_permissions: [],
      expanded_feature_ids: [],
    },
    member_access_errors: {},
    invite_user: {
      email: "",
      role: "user",
      invite_method: "email",
    },
    invite_errors: {},
    latest_invitation: null,
    new_user: {
      first_name: "",
      last_name: "",
      email: "",
      phone_number: "",
      role: "user",
    },
    new_user_errors: {},
    bulk_members: [],
    bulk_member_errors: {
      form: "",
      members: [],
    },
    existing_company_member: {
      member_id: "",
      role: "user",
    },
    existing_company_member_search: "",
    existing_company_member_errors: {},
  }),
  computed: {
    organization() {
      return normalize_organization_profile(
        this.active_profile?.company || this.active_profile || {},
        this.user_details || {},
        this.saved_branding
      );
    },
    management_tabs() {
      const tabs = [
        { key: "members", label: "Members" },
        { key: "invitations", label: "Invitations" },
        { key: "overview", label: "Overview" },
      ];

      if (this.organization.is_private) {
        tabs.splice(1, 0, { key: "companies", label: "Companies" });
        tabs.splice(3, 0, { key: "approvals", label: "Approval Requests" });
      }

      return tabs;
    },
    branding_style() {
      const style = {};
      if (this.organization.primary_color) {
        style["--primary-color"] = this.organization.primary_color;
        style["--accent-color"] = this.organization.primary_color;
      }
      if (this.organization.secondary_color) {
        style["--secondary-color"] = this.organization.secondary_color;
      }
      if (this.organization.tertiary_color) {
        style["--tertiary-color"] = this.organization.tertiary_color;
      }
      if (this.organization.primary_color) {
        const secondary = this.organization.secondary_color || this.organization.primary_color;
        const tertiary = this.organization.tertiary_color || secondary;
        style["--banner-gradient"] = `linear-gradient(135deg, ${this.organization.primary_color} 0%, ${secondary} 55%, ${tertiary} 100%)`;
        style["--client-bg-color"] = style["--banner-gradient"];
      }
      return style;
    },
    current_member() {
      return get_organization_member_by_user_details(
        this.organization_members,
        this.user_details || {}
      );
    },
    can_manage_members() {
      return (
        is_organization_admin_role(this.user_details) ||
        String(this.current_member?.role || "").toLowerCase() === "manager" ||
        member_has_feature_permission(this.current_member, "members")
      );
    },
    can_manage_companies() {
      return is_organization_admin_role(this.user_details);
    },
    filtered_members() {
      const query = this.normalize_query(this.search_query);
      if (!query) return this.accepted_members;
      return this.accepted_members.filter((member) => this.user_matches_query(member, query));
    },
    accepted_members() {
      return this.organization_members.filter(member_is_accepted);
    },
    assignable_company_members() {
      if (!this.is_company_member_target()) return [];
      const target_company_id = Number(this.selected_company_id);
      const organization_id = Number(this.organization.company_id || this.organization.id);
      const assigned_emails = new Set(
        this.accepted_members
          .filter((member) => Number(member.company_id) === target_company_id)
          .map((member) => String(member.email || "").trim().toLowerCase())
          .filter(Boolean)
      );

      return this.accepted_members.filter((member) => {
        const email = String(member.email || "").trim().toLowerCase();
        return Number(member.company_id) === organization_id
          && email
          && !assigned_emails.has(email);
      });
    },
    filtered_assignable_company_members() {
      const query = this.normalize_query(this.existing_company_member_search);
      if (!query) return this.assignable_company_members;
      return this.assignable_company_members.filter((member) => this.user_matches_query(member, query));
    },
    member_stats() {
      return this.organization_members.reduce((stats, member) => {
        stats.total += 1;
        if (member_is_active(member)) stats.active += 1;
        if (member_is_pending(member)) stats.pending += 1;
        return stats;
      }, {
        total: 0,
        admins: 0,
        active: 0,
        pending: 0,
      });
    },
    pending_invitations() {
      return this.organization_members.filter(member_is_pending);
    },
    organization_invitations() {
      return this.organization_members.filter(
        (member) => member_is_pending(member) || member_is_rejected(member)
      );
    },
    company_stats() {
      return get_company_stats(this.organization_companies);
    },
  },
  mounted() {
    this.user_details = get_user_details() || {};
    this.saved_branding = this.get_saved_branding();
  },
  beforeUnmount() {
    this.destroy_members_datatable();
    this.destroy_companies_datatable();
    this.destroy_invitations_datatable();
  },
  watch: {
    management_tabs(tabs) {
      if (!tabs.some(tab => tab.key === this.active_management_tab)) {
        this.active_management_tab = "members";
      }
    },
    active_management_tab(value) {
      if (value === "members") this.refresh_members_datatable();
      if (value === "companies") this.refresh_companies_datatable();
      if (value === "invitations") this.refresh_invitations_datatable();
    },
    search_query(value) {
      this.search_members_table(value);
    },
    can_manage_members() {
      this.$nextTick(() => {
        this.destroy_members_datatable();
        this.destroy_companies_datatable();
        this.destroy_invitations_datatable();
        this.refresh_members_datatable();
        this.refresh_companies_datatable();
        this.refresh_invitations_datatable();
      });
    },
  },
  methods: {
    format_invitation_date,
    ...organization_members_context_methods,
    ...organization_members_form_methods,
    ...organization_members_access_methods,
    ...organization_members_company_methods,
  },
};
